"use client";

import { useEffect, useState } from "react";

import { useRouter, useSearchParams } from "next/navigation";

import { Input } from "@heroui/input";
import { Search } from "lucide-react";

import { EventKeys } from "@/lib/observability/catalog";
import { EventPublisher } from "@/lib/observability/events";
import { OrganizationTerminology } from "@/lib/services/terminologyService";

interface AreasSearchAndFilterProps {
  searchQuery: string;
  selectedState: string;
  sortBy: string;
  states: { state: string }[];
  terminology: OrganizationTerminology;
}

type PushFn = (updates: Record<string, string | null>) => void;

function publishAreaSearch(value: string, push: PushFn) {
  push({ q: value || null });
  void EventPublisher.publishEvent(EventKeys.AREAS_SEARCH, { query: value });
}

function publishStateFilter(value: string, push: PushFn) {
  push({ state: value || null });
  void EventPublisher.publishEvent(EventKeys.AREAS_FILTER_STATE, {
    state: value,
  });
}

function publishSortFilter(value: string, push: PushFn) {
  push({ sort: value || null });
  void EventPublisher.publishEvent(EventKeys.AREAS_SORT, { sort: value });
}

function applyParamUpdates(
  current: string,
  updates: Record<string, string | null>,
): string {
  const params = new URLSearchParams(current);

  Object.entries(updates).forEach(([key, value]) => {
    if (value === null) {
      params.delete(key);
    } else {
      params.set(key, value);
    }
  });

  return `?${params.toString()}`;
}

function StateOption({ states }: { states: { state: string }[] }) {
  if (states.length <= 1) return null;

  return <option value="state">State A-Z</option>;
}

interface StateFilterProps {
  selectedState: string;
  states: { state: string }[];
  onChange: (value: string) => void;
}

function StateFilter({ onChange, selectedState, states }: StateFilterProps) {
  if (states.length <= 1) return null;

  return (
    <select
      className="px-4 py-2.5 border border-default-300 rounded-lg bg-background text-sm font-medium hover:border-primary-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-colors"
      value={selectedState}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">All States</option>
      {states.map((s) => (
        <option key={s.state} value={s.state}>
          {s.state}
        </option>
      ))}
    </select>
  );
}

interface ActiveFiltersProps {
  searchQuery: string;
  selectedState: string;
  onClearSearch: () => void;
  onClearState: () => void;
}

function ActiveFilters({
  onClearSearch,
  onClearState,
  searchQuery,
  selectedState,
}: ActiveFiltersProps) {
  if (!searchQuery && !selectedState) return null;

  return (
    <div className="mt-4 flex flex-wrap gap-2">
      {searchQuery && (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-800">
          Search: &quot;{searchQuery}&quot;
          <button
            className="ml-2 text-primary-600 hover:text-primary-800"
            onClick={onClearSearch}
          >
            ×
          </button>
        </span>
      )}
      {selectedState && (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-secondary-100 text-secondary-800">
          State: {selectedState}
          <button
            className="ml-2 text-secondary-600 hover:text-secondary-800"
            onClick={onClearState}
          >
            ×
          </button>
        </span>
      )}
    </div>
  );
}

export default function AreasSearchAndFilter({
  searchQuery,
  selectedState,
  sortBy,
  states,
  terminology,
}: AreasSearchAndFilterProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [searchValue, setSearchValue] = useState(searchQuery);

  useEffect(() => {
    setSearchValue(searchQuery);
  }, [searchQuery]);

  const pushParams = (updates: Record<string, string | null>) => {
    router.push(applyParamUpdates(searchParams.toString(), updates));
  };

  const handleSearch = (value: string) => publishAreaSearch(value, pushParams);
  const handleStateChange = (value: string) =>
    publishStateFilter(value, pushParams);
  const handleSortChange = (value: string) =>
    publishSortFilter(value, pushParams);
  const clearSearchFilter = () => {
    pushParams({ q: null });
    setSearchValue("");
    void EventPublisher.publishEvent(EventKeys.AREAS_CLEAR_SEARCH, {});
  };
  const clearStateFilter = () => {
    pushParams({ state: null });
    void EventPublisher.publishEvent(EventKeys.AREAS_CLEAR_STATE, {});
  };

  return (
    <div className="bg-white border border-default-200 rounded-xl p-6 mb-8 shadow-sm">
      <div className="flex flex-col lg:flex-row gap-4 items-center">
        {/* Search Input */}
        <div className="flex-1 w-full lg:w-auto">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-default-400 h-5 w-5 z-10" />
            <Input
              className="pl-10"
              placeholder="Search areas by name or state..."
              size="lg"
              value={searchValue}
              variant="bordered"
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearch(searchValue);
                }
              }}
            />
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-3">
          <StateFilter
            selectedState={selectedState}
            states={states}
            onChange={handleStateChange}
          />

          {/* Sort Options */}
          <select
            className="px-4 py-2.5 border border-default-300 rounded-lg bg-background text-sm font-medium hover:border-primary-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-colors"
            value={sortBy}
            onChange={(e) => handleSortChange(e.target.value)}
          >
            <option value="name">Name A-Z</option>
            <option value="name-desc">Name Z-A</option>
            <StateOption states={states} />
            <option value="needs">Most {terminology.need.plural}</option>
          </select>
        </div>
      </div>

      <ActiveFilters
        searchQuery={searchQuery}
        selectedState={selectedState}
        onClearSearch={clearSearchFilter}
        onClearState={clearStateFilter}
      />
    </div>
  );
}
